import { ReactNode, useEffect, cloneElement, isValidElement } from "react";
import ErrorBoundary from "./ErrorBoundary";
import { useCurrencyMode } from "../context/CurrencyContext";

interface PageWrapperProps {
  children: ReactNode;
  pageName?: string;
}

/**
 * PageWrapper - Wraps individual page routes with ErrorBoundary for better error isolation
 *
 * This ensures that if one page crashes, it doesn't take down the entire app.
 * The outer app-level ErrorBoundary acts as a last resort fallback.
 *
 * Usage:
 *   <Route path="/admin/dashboard" element={
 *     <PageWrapper pageName="Operations Dashboard">
 *       <OperationsDashboard />
 *     </PageWrapper>
 *   } />
 */
export const PageWrapper = ({ children, pageName }: PageWrapperProps) => {
  // Subscribe to the currency store so a USD ⇄ AUD flip re-renders THIS page in
  // place — no remount (scroll + chart animations preserved). This MUST live
  // here, not at the App root: React Router v7 memoizes the matched route by
  // location, so an App-level re-render never reaches the page. cloneElement()
  // below produces a fresh child element each render, defeating React's
  // referential-equality bailout (the raw `children` prop is a stable element
  // created in App, so returning it as-is would skip the page's re-render).
  useCurrencyMode();

  useEffect(() => {
    document.title = pageName ? `${pageName} — Altara` : "Altara CRM";
  }, [pageName]);

  // Custom fallback that includes page name for better debugging
  const fallback = pageName ? (
    <div className="min-h-screen flex items-center justify-center bg-black/[0.03] px-4">
      <div className="max-w-md w-full bg-black/[0.05] rounded-lg shadow-lg p-8 text-center">
        <div className="mx-auto w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mb-6">
          <svg
            className="w-8 h-8 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-semibold text-glass mb-2">Error Loading {pageName}</h2>
        <p className="text-glass-secondary mb-6">
          We encountered an issue loading this page. Try refreshing or navigating to another page.
        </p>

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-glass rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Refresh Page
          </button>
          <button
            onClick={() => (window.location.hash = "#/admin/dashboard")}
            className="px-4 py-2 bg-black/[0.05] text-glass-secondary rounded-md hover:bg-black/[0.06] transition-colors font-medium"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  ) : undefined;

  // cloneElement → fresh element ref each render → page re-renders in place on
  // a currency flip (see the note above). Non-element children pass through.
  const content = isValidElement(children) ? cloneElement(children) : children;

  return <ErrorBoundary fallback={fallback}>{content}</ErrorBoundary>;
};
